import { useTranslation } from "react-i18next";
import { useBilingual } from "@/lib/bilingual";

const MarketsSection = () => {
  const { t } = useTranslation();
  const b = useBilingual();

  // Export markets. Four regions endorsed by Forwaro (David, 2026-07-18):
  // Middle East / Europe / Americas kept as representative-market examples;
  // Asia country list supplied directly. Do not re-flag as fabricated.
  const markets = [
    {
      region: b("Middle East", "中東"),
      countries: b(
        "UAE · Saudi Arabia · Qatar · Kuwait · Oman",
        "阿聯 · 沙烏地阿拉伯 · 卡達 · 科威特 · 阿曼",
      ),
      icon: "ri-map-pin-2-line",
      color: "bg-amber-50 border-amber-200",
    },
    {
      region: b("Europe", "歐洲"),
      countries: b(
        "Germany · United Kingdom · France · Italy · Spain",
        "德國 · 英國 · 法國 · 義大利 · 西班牙",
      ),
      icon: "ri-earth-line",
      color: "bg-stone-50 border-stone-200",
    },
    {
      region: b("Americas", "美洲"),
      countries: b(
        "United States · Canada · Latin America",
        "美國 · 加拿大 · 拉丁美洲",
      ),
      icon: "ri-building-line",
      color: "bg-amber-50 border-amber-200",
    },
    {
      // Asia — countries confirmed by Forwaro (David, 2026-07-18).
      region: b("Asia", "亞洲"),
      countries: b(
        "Japan · South Korea · India · Vietnam · Malaysia · Singapore · Russia · Philippines · Indonesia",
        "日本 · 韓國 · 印度 · 越南 · 馬來西亞 · 新加坡 · 俄羅斯 · 菲律賓 · 印尼",
      ),
      icon: "ri-compass-3-line",
      color: "bg-stone-50 border-stone-200",
    },
  ];

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-amber-700 text-xs font-semibold tracking-widest uppercase mb-3">
            {t("home.markets.kicker")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-3">
            {t("home.markets.heading")}
          </h2>
          <p className="text-stone-500 text-sm max-w-lg mx-auto">
            {t("home.markets.description")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-10 md:mb-14">
          {markets.map((m) => (
            <div key={m.region} className={`border rounded-2xl p-5 md:p-8 ${m.color}`}>
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-white text-amber-700 mb-3 md:mb-5">
                <i className={`${m.icon} text-xl md:text-2xl`} />
              </div>
              <h3 className="text-stone-900 font-bold text-lg md:text-xl mb-1 md:mb-2">{m.region}</h3>
              <p className="text-stone-500 text-sm leading-relaxed">{m.countries}</p>
            </div>
          ))}
        </div>

        {/* Process Steps — describes Forwaro's standard B2B export flow.
            Language kept neutral, no fabricated turnaround commitments. */}
        <div className="bg-stone-900 rounded-3xl p-6 md:p-14">
          <div className="text-center mb-10">
            <h3 className="text-white text-2xl md:text-3xl font-bold mb-2">
              {t("home.process.heading")}
            </h3>
            <p className="text-stone-400 text-sm">{t("home.process.description")}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                step: "01",
                title: b("Inquiry & Quote", "詢價與報價"),
                desc: b(
                  "Send your requirements through our contact form. Our export team replies with an FOB quote and spec confirmation.",
                  "透過聯絡表單送出需求，外銷團隊將回覆 FOB 報價與規格確認。",
                ),
                icon: "ri-mail-send-line",
              },
              {
                step: "02",
                title: b("Sample Approval", "樣品確認"),
                desc: b(
                  "We produce samples for your approval, including your selected finishes and materials, before mass production begins.",
                  "批量生產前，我們先打樣供您確認，包含您選定的表面處理與材質。",
                ),
                icon: "ri-box-3-line",
              },
              {
                step: "03",
                title: b("Production", "生產製造"),
                desc: b(
                  "Your order enters the Forwaro production line in Dongguan with full QC oversight at every stage.",
                  "您的訂單進入 Forwaro 東莞產線，全程由品管團隊逐站把關。",
                ),
                icon: "ri-tools-line",
              },
              {
                step: "04",
                title: b("Export & Delivery", "出口與交付"),
                desc: b(
                  "We prepare export documentation, packaging and container loading to your destination port.",
                  "我們準備出口文件、包裝與櫃運，將貨品送抵您指定的目的港。",
                ),
                icon: "ri-ship-line",
              },
            ].map((step) => (
              <div key={step.step} className="text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-700/20 text-amber-500 mx-auto mb-4">
                  <i className={`${step.icon} text-2xl`} />
                </div>
                <p className="text-amber-500 text-xs font-bold tracking-widest mb-2">
                  {b("STEP", "步驟")} {step.step}
                </p>
                <p className="text-white font-semibold text-sm mb-2">{step.title}</p>
                <p className="text-stone-500 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketsSection;
